import {
  CheckCircle,
  ChevronDown,
  Code,
  Download,
  Lock,
  Server,
  Smartphone,
} from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import heroBg from "../assets/hero.jpg";
import { FaGooglePlay } from "react-icons/fa6";
import { DiAndroid } from "react-icons/di";

const Home = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="min-h-screen">
      <section className="relative md:h-screen overflow-hidden flex pb-32 pt-10">
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat">
          <img
            src={heroBg}
            alt="Maldroid Background"
            className="w-full h-full object-cover opacity-100"
          />
        </div>

        <div className="absolute inset-0 z-0 bg-gradient-to-br from-white/95 via-blue-50/80 to-teal-50/80" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <div className="inline-block bg-secondary/10 text-secondary px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              Official Thesis Project 2026
            </div> */}
            <h1 className="text-5xl md:text-6xl text-center md:text-left font-bold text-gray-900 leading-tight mb-6">
              Advanced <span className="text-primary">APK Analysis</span> using
              Machine Learning
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              MALDROID provides real-time malware detection by analyzing
              permissions, API calls, and opcodes using Random Forest.
            </p>

            <p className="text-sm text-gray-600 mb-1 leading-relaxed">
              Available only on Android
            </p>
            <div className="flex flex-col sm:flex-row gap-4 relative z-50">
              <div
                className="relative z-50 group w-full sm:w-auto"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <button className="flex items-center gap-3 bg-primary hover:bg-blue-800 text-white px-8 py-3.5 rounded-lg font-medium shadow-lg transition-all w-full sm:w-auto min-w-[240px] justify-between">
                  <div className="flex items-center gap-3">
                    <Download className="h-5 w-5" />
                    <span>Download MALDROID</span>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-300 ${isHovered ? "rotate-180" : ""}`}
                  />
                </button>

                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                    >
                      <a
                        href="/maldroid-alpha-v2.apk"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors cursor-pointer border-b border-gray-50 group/item"
                      >
                        <div className="bg-green-100 p-2 rounded-full text-green-600 group-hover/item:bg-green-200 transition-colors">
                          <DiAndroid className="h-4 w-4" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-sm text-gray-800">
                            Download APK
                          </div>
                          <div className="text-[10px] text-gray-500">
                            Direct Install
                          </div>
                        </div>
                      </a>

                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 cursor-not-allowed opacity-70">
                        <div className="bg-gray-200 p-2 rounded-full text-gray-500">
                          <FaGooglePlay className="h-4 w-4" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-sm text-gray-500">
                            Google Play
                          </div>
                          <div className="text-[10px] font-bold text-orange-500 bg-orange-100 px-1.5 py-0.5 rounded inline-block">
                            Beta Testing
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link
                to="/about"
                className="bg-white/90 backdrop-blur-sm text-gray-700 border border-gray-200 px-8 py-3.5 rounded-lg font-medium hover:bg-gray-50 transition-all text-center"
              >
                Read Abstract
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 relative z-10">
              <div className="flex items-center justify-between mb-6 border-b pb-4">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="text-xs text-gray-400 font-mono">
                  SCAN_STATUS: SECURE
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-blue-50 rounded-lg">
                  <Server className="text-primary h-5 w-5 mr-3" />
                  <div>
                    <h4 className="text-sm font-bold text-primary">
                      Static/Dynamic Analysis
                    </h4>
                    <p className="text-xs text-gray-600">
                      Permissions, OpCodes, & API Extraction
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-teal-50 rounded-lg">
                  <Code className="text-secondary h-5 w-5 mr-3" />
                  <div>
                    <h4 className="text-sm font-bold text-secondary">
                      Machine Learning
                    </h4>
                    <p className="text-xs text-gray-600">
                      Random Forest Classifier
                    </p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="text-green-600 h-5 w-5 mr-3" />
                  <div>
                    <h4 className="text-sm font-bold text-green-700">
                      High Accuracy
                    </h4>
                    <p className="text-xs text-gray-600">Accuracy: 93.12%</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-10 -right-10 w-64 h-64 bg-secondary/10 rounded-full blur-3xl -z-10"></div>
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Core System Capabilities
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Combines static/hybrid analysis and machine learning to overcome
              limitations of traditional signature-based detection.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Real-Time Monitoring",
                icon: <Smartphone className="h-8 w-8 text-background" />,
                desc: "Proactive solution that scans APKs in real-time to neutralize malicious activities immediately.",
              },
              {
                title: "Privacy Focused",
                icon: <Lock className="h-8 w-8 text-background" />,
                desc: "Features 'Clear cached APK analysis' and 'Auto-delete scan logs' to ensure user data remains private.",
              },
              {
                title: "Advanced ML Models",
                icon: <Server className="h-8 w-8 text-background" />,
                desc: "Utilizes Random Forest optimized with Relevance-Redundancy Feature Selection.",
              },
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -5 }}
                className="p-8 rounded-xl bg-zinc-300 border border-gray-500 shadow-sm hover:shadow-md transition-all"
              >
                <div className="bg-secondary w-14 h-14 rounded-lg flex items-center justify-center shadow-sm mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
